// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "Terminals from Konvo" on a machine's card in Settings › Connections › Agents (design/
// agents-terminals s1-s2; JP 2026-10-22): one switch, on by default, that lets its owner open the
// machine's terminals from Konvo, see them live and type into them. Only the owner, never people a
// chat is shared with. Konvo holds the machine to it, and so does the machine's own cap
// (`konvo-agents config --terminals off`), shown here when set.

import shareStyles from '@app/features/konvo_agents/components/AgentShare.module.css';
import {shareErrorMessage} from '@app/features/konvo_agents/components/AgentShareBits';
import AgentShares from '@app/features/konvo_agents/state/AgentShares';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import type {AgentMachineView} from '@fluxer/schema/src/domains/konvo/Agents';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useState} from 'react';

/** Whether the card shows the row: the machine sends and its konvo-agents has terminals (or capped them). */
export const showsTerminalsSetting = (machine: AgentMachineView) =>
	machine.limits.send &&
	(machine.limits.terminalsCap !== undefined || machine.tools.some((tool) => tool.can.terminalSessions === true));

export const AgentMachineTerminals = observer(({machine}: {machine: AgentMachineView}) => {
	const {i18n, t} = useLingui();
	const [saving, setSaving] = useState(false);
	const on = machine.limits.terminals !== false;
	const capped = machine.limits.terminalsCap === 'off';
	const name = machine.name;
	const save = async (value: boolean) => {
		setSaving(true);
		try {
			await AgentShares.saveMachineSettings(machine.id, {terminals: value});
		} catch (error) {
			ToastCommands.error(shareErrorMessage(i18n, error));
		} finally {
			setSaving(false);
		}
	};
	return (
		<dl className={clsx(shareStyles.scope, shareStyles.settings)} data-flx="konvo-agents.machine.terminals">
			<dt>
				<Trans comment="Agents Settings: row about the machine's terminals in Konvo.">Terminals</Trans>
			</dt>
			<dd>
				<div data-flx="konvo-agents.machine.terminals.switch">
					<Switch
						label={t({
							message: 'Terminals from Konvo',
							comment:
								'Agents Settings: switch that lets the owner open and type into the machine’s terminals from Konvo.',
						})}
						description={t({
							message: `Open terminals for a chat, see them live and type into them, the ones you opened in T3 too. What you type runs on ${name} straight away. Only you, never people you share a chat with.`,
							comment: 'Agents Settings: what the Terminals from Konvo switch means. The name is the computer.',
						})}
						value={on && !capped}
						disabled={saving || capped}
						onChange={(value) => void save(value)}
						compact
					/>
				</div>
				{capped && (
					<span className={shareStyles.settingsHint} data-flx="konvo-agents.machine.terminals.cap">
						<Trans comment="Agents Settings: the machine's owner turned terminals off on the machine itself. {name} is the computer.">
							Terminals are off on {name} itself (konvo-agents config --terminals on there to allow them).
						</Trans>
					</span>
				)}
			</dd>
		</dl>
	);
});
